import { useEffect, useRef, useState } from "react";
import { Button } from "@akuna/ui";
import { Camera, Upload, X, User as UserIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { isNative, pickPhoto } from "@/lib/native";

interface ProfilePhotoUploadProps {
  value?: string | null;
  onChange: (file: File | null) => void;
  onRemove?: () => void;
  disabled?: boolean;
}

export function ProfilePhotoUpload({
  value,
  onChange,
  onRemove,
  disabled,
}: ProfilePhotoUploadProps) {
  const { t } = useTranslation("settings");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [preview, setPreview] = useState<string | null>(value || null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Atualizar preview quando value mudar (do servidor)
  useEffect(() => {
    setPreview(value || null);
  }, [value]);

  // Validates and applies a selected image file (preview + onChange). Shared by
  // the web file input and the native photo picker.
  const applySelectedFile = (file: File) => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      alert(t("profilePhotoInvalidType"));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert(t("profilePhotoTooLarge"));
      return;
    }

    onChange(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    applySelectedFile(file);
  };

  // On native, both "choose file" and "take photo" open the native picker,
  // which lets the user choose between camera and gallery.
  const handleNativePick = async () => {
    if (disabled) return;
    const file = await pickPhoto();
    if (file) applySelectedFile(file);
  };

  const handleChooseFileClick = () => {
    if (isNative()) {
      void handleNativePick();
      return;
    }
    fileInputRef.current?.click();
  };

  const handleTakePhotoClick = () => {
    if (disabled) return;
    if (isNative()) {
      void handleNativePick();
      return;
    }
    setCameraError(null);
    setIsCameraOpen(true);
  };

  const handleRemove = () => {
    onChange(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    stopCamera();
    onRemove?.();
  };

  const stopCamera = () => {
    setCameraError(null);
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  useEffect(() => {
    if (!isCameraOpen) {
      stopCamera();
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("profilePhotoCameraUnsupported"));
      return;
    }

    let isMounted = true;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" }, // Front camera for profile photos
        });
        if (!isMounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        setCameraStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {
            /* ignore autoplay issues */
          });
        }
      } catch (error) {
        console.error("Erro ao acessar a câmera:", error);
        setCameraError(t("profilePhotoCameraAccessError"));
      }
    };

    startCamera();

    return () => {
      isMounted = false;
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraOpen]);

  const handleCapture = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setCameraError(t("profilePhotoCameraInitializing"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");

    if (!context) {
      setCameraError(t("profilePhotoCaptureUnavailable"));
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError(t("profilePhotoCaptureProcessError"));
          return;
        }

        const file = new File([blob], `profile-photo-${Date.now()}.jpg`, {
          type: blob.type || "image/jpeg",
        });

        onChange(file);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        setPreview(dataUrl);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (cameraInputRef.current) cameraInputRef.current.value = "";
        stopCamera();
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Avatar Preview */}
      <div className="relative">
        {preview ? (
          <img
            src={preview}
            alt={t("profilePhotoAlt")}
            className="h-32 w-32 rounded-full object-cover border-4 border-border shadow-lg"
          />
        ) : (
          <div className="h-32 w-32 rounded-full bg-muted border-4 border-border flex items-center justify-center shadow-lg">
            <UserIcon className="h-16 w-16 text-muted-foreground" />
          </div>
        )}
        {preview && !disabled && (
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Upload Buttons */}
      {!preview && (
        <div className="flex flex-col sm:flex-row gap-2 w-full max-w-md">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
            disabled={disabled}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleFileChange}
            className="hidden"
            disabled={disabled}
          />

          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={handleChooseFileClick}
            disabled={disabled}
          >
            <Upload className="h-4 w-4 mr-2" />
            {t("profilePhotoChooseFile")}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={handleTakePhotoClick}
            disabled={disabled}
          >
            <Camera className="h-4 w-4 mr-2" />
            {t("profilePhotoTakePhoto")}
          </Button>
        </div>
      )}

      {preview && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleChooseFileClick}
          disabled={disabled}
        >
          <Upload className="h-4 w-4 mr-2" />
          {t("profilePhotoChangePhoto")}
        </Button>
      )}

      {/* Camera Dialog */}
      <Dialog
        open={isCameraOpen}
        onOpenChange={(open) => {
          if (!open) stopCamera();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("profilePhotoCaptureTitle")}</DialogTitle>
            <DialogDescription>
              {t("profilePhotoCaptureDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {cameraError ? (
              <p className="text-sm text-destructive">{cameraError}</p>
            ) : (
              <div className="aspect-square w-full max-w-sm mx-auto overflow-hidden rounded-full border bg-black">
                <video
                  ref={videoRef}
                  className="h-full w-full object-cover"
                  playsInline
                  muted
                  autoPlay
                />
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={stopCamera}
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="w-full sm:w-auto"
              onClick={handleCapture}
              disabled={!!cameraError || !cameraStream}
            >
              {t("profilePhotoCaptureButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-xs text-muted-foreground text-center">
        {t("profilePhotoHint")}
      </p>
    </div>
  );
}

